<script setup lang="ts">
    import { useTheme } from 'vuetify';
    import { useMenuStore } from '@/stores/menu';
    import { INavMenuLeaf } from '@/types';

    const menuStore = useMenuStore();
    const theme = useTheme();
    const router = useRouter();
    const { t } = useI18n();

    const query = ref('');
    const isExpanded = ref(false);
    const highlightedIndex = ref(-1);
    const inputRef = ref<HTMLInputElement | null>(null);
    const rootRef = ref<HTMLElement | null>(null);
    const panelRef = ref<HTMLElement | null>(null);
    const dropdownRef = ref<HTMLElement | null>(null);
    const dropdownStyle = ref<Record<string, string>>({});

    const isRecentMode = computed(() => !query.value.trim());

    const results = computed((): INavMenuLeaf[] => {
        const q = query.value.trim().toLowerCase();
        if (!q) return menuStore.recentMenuLeaves;

        return menuStore.navMenuLeaves.filter((item) => matchesQuery(item, q));
    });

    const showResults = computed(() => isExpanded.value && (results.value.length > 0 || !isRecentMode.value));

    function matchesQuery(item: INavMenuLeaf, q: string) {
        const titleLabel = t('menu.' + item.title);

        return titleLabel.toLowerCase().includes(q) || item.title.toLowerCase().includes(q);
    }

    function updateDropdownPosition() {
        const rect = panelRef.value?.getBoundingClientRect();
        if (!rect) return;

        dropdownStyle.value = {
            top: `${rect.bottom + 4}px`,
            left: `${rect.left}px`,
            minWidth: '280px',
        };
    }

    async function openSearch() {
        if (isExpanded.value) return;

        isExpanded.value = true;
        highlightedIndex.value = -1;
        await nextTick();
        updateDropdownPosition();
        inputRef.value?.focus();
    }

    function closeSearch() {
        query.value = '';
        isExpanded.value = false;
        highlightedIndex.value = -1;
        inputRef.value?.blur();
    }

    function goTo(item: INavMenuLeaf) {
        closeSearch();
        void router.push({ name: item.link, params: item.params ?? {} });
    }

    function onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeSearch();
            return;
        }

        if (!results.value.length) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            highlightedIndex.value = highlightedIndex.value < 0 ? 0 : Math.min(highlightedIndex.value + 1, results.value.length - 1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            highlightedIndex.value = highlightedIndex.value < 0 ? results.value.length - 1 : Math.max(highlightedIndex.value - 1, 0);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const item = highlightedIndex.value >= 0 ? results.value[highlightedIndex.value] : undefined;
            if (item) goTo(item);
        }
    }

    function onDocumentPointerDown(event: PointerEvent) {
        if (!isExpanded.value) return;

        const target = event.target as Node;
        if (rootRef.value?.contains(target) || dropdownRef.value?.contains(target)) return;

        closeSearch();
    }

    watch(results, () => {
        highlightedIndex.value = !isRecentMode.value && results.value.length === 1 ? 0 : -1;
    });

    watch(highlightedIndex, () => {
        nextTick(() => {
            dropdownRef.value?.querySelector('.is-highlighted')?.scrollIntoView({ block: 'nearest' });
        });
    });

    onMounted(() => {
        document.addEventListener('pointerdown', onDocumentPointerDown);
    });

    onBeforeUnmount(() => {
        document.removeEventListener('pointerdown', onDocumentPointerDown);
    });
</script>

<template>
    <div ref="rootRef" class="app-menu-search" :class="{ expanded: isExpanded }">
        <v-btn v-show="!isExpanded" icon variant="text" size="20" class="search-toggle-btn" :data-tooltip="t('menu.searchPlaceholder')" @click="openSearch">
            <v-icon :icon="mdiTabSearch" size="20" :color="theme.name.value === 'dark' ? 'white' : 'black'" />
        </v-btn>

        <div ref="panelRef" class="search-panel">
            <div class="search-field">
                <v-icon :icon="mdiMagnify" size="18" class="search-icon" />
                <input
                    ref="inputRef"
                    v-model="query"
                    type="text"
                    class="search-input"
                    :placeholder="t('menu.searchPlaceholder')"
                    autocomplete="off"
                    autocapitalize="off"
                    spellcheck="false"
                    @keydown="onKeydown"
                />
                <v-btn icon variant="text" size="x-small" class="search-close-btn" :ripple="false" tabindex="-1" @mousedown.prevent="closeSearch">
                    <v-icon :icon="mdiClose" size="16" />
                </v-btn>
            </div>
        </div>
    </div>

    <Teleport to="body">
        <div
            v-show="showResults"
            ref="dropdownRef"
            class="app-menu-search-dropdown"
            :class="{ 'v-theme--dark': theme.name.value === 'dark' }"
            :style="dropdownStyle"
            @mousedown.prevent
        >
            <v-list class="submenu pa-1" nav>
                <v-list-subheader
                    v-if="isRecentMode && results.length"
                    :class="theme.name.value === 'dark' ? 'text-white' : 'text-black'"
                    class="pl-2 font-weight-black text-uppercase pt-1"
                >
                    {{ t('menu.searchRecent') }}
                </v-list-subheader>

                <v-list-item v-if="!results.length" class="submenu-item">
                    <v-list-item-title class="font-weight-medium font13 text-medium-emphasis">
                        {{ t('common.notFound') }}
                    </v-list-item-title>
                </v-list-item>

                <router-link
                    v-for="(item, index) in results"
                    :key="item.link"
                    :to="{ name: item.link, params: item.params ?? {} }"
                    class="text-black link-plain"
                    tabindex="-1"
                    @click="closeSearch"
                    @mouseenter="highlightedIndex = index"
                >
                    <v-list-item
                        nav
                        tabindex="-1"
                        base-color="grey"
                        density="compact"
                        class="submenu-item"
                        :class="{ 'is-highlighted': index === highlightedIndex }"
                    >
                        <v-list-item-title :class="theme.name.value === 'dark' ? 'text-white' : 'text-black'" class="font-weight-medium font13">
                            <template v-if="item.parentTitle">
                                <span class="menu-search-parent">{{ t('menu.' + item.parentTitle) }}</span>
                                <span class="menu-search-sep">|</span>
                            </template>
                            <span>{{ t('menu.' + item.title) }}</span>
                        </v-list-item-title>
                    </v-list-item>
                </router-link>
            </v-list>
        </div>
    </Teleport>
</template>

<style lang="scss" scoped>
    .app-menu-search {
        position: relative;
        display: flex;
        align-items: center;
        flex-shrink: 0;
        z-index: 10;
        margin-left: 4px;
        margin-right: 8px;
    }

    .search-toggle-btn {
        transition: opacity 0.15s ease;
    }

    .search-panel {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        max-width: 0;
        background: rgb(var(--v-theme-surface));
        border: 1px solid transparent;
        border-radius: 6px;
        box-shadow: none;
        opacity: 0;
        overflow: hidden;
        pointer-events: none;
        white-space: nowrap;
        transition:
            max-width 0.25s ease,
            opacity 0.2s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease;

        .expanded & {
            max-width: 280px;
            opacity: 1;
            pointer-events: auto;
            border-color: rgba(var(--v-border-color), var(--v-border-opacity));
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        }

        .expanded &:focus-within {
            border-color: rgba(var(--v-theme-tbGreen), 0.55);
        }
    }

    .search-field {
        display: flex;
        align-items: center;
        gap: 4px;
        height: 34px;
        padding: 0 6px;
        min-width: 260px;
    }

    .search-icon {
        color: rgba(var(--v-theme-on-surface), 0.5);
        flex-shrink: 0;
    }

    .search-input {
        flex: 1;
        min-width: 0;
        border: none;
        outline: none;
        background: transparent;
        font-size: 13px;
        color: rgb(var(--v-theme-on-surface));
        height: 100%;
        padding: 0 4px;

        &::placeholder {
            color: rgba(var(--v-theme-on-surface), 0.4);
        }
    }

    .search-close-btn {
        width: 22px !important;
        height: 22px !important;
        color: rgba(var(--v-theme-on-surface), 0.5) !important;
        flex-shrink: 0;

        &:hover {
            color: rgb(var(--v-theme-on-surface)) !important;
        }
    }

    .app-menu-search-dropdown {
        position: fixed;
        z-index: 2400;
    }

    .menu-search-parent {
        color: rgba(var(--v-theme-on-surface), 0.55);
        font-weight: 500;
    }

    .menu-search-sep {
        color: rgba(var(--v-theme-on-surface), 0.55);
        font-weight: 500;
        margin: 0 4px;
    }

    .submenu {
        min-width: 280px;
        max-height: 360px;
        overflow-y: auto;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .submenu-item {
        min-height: 32px;
    }

    .submenu-item :deep(.v-list-item-title) {
        line-height: 1.2;
    }

    .v-list-item {
        transition: all 0.2s ease;

        &:hover,
        &.is-highlighted {
            background: rgba(var(--v-theme-tbGreen), 0.15) !important;

            .v-list-item-title {
                color: rgb(var(--v-theme-tbGreen)) !important;
            }
        }
    }

    .v-theme--dark {
        .v-list {
            background: #2b2d31;
        }

        .v-list-item {
            &:hover,
            &.is-highlighted {
                background: rgba(var(--v-theme-tbGreen), 0.25) !important;
            }
        }
    }
</style>
